import { isBrightcoveUrl, isBroadcomVideoUrl } from "@shared/sharedUtils";
import { isValidEmail, isValidUrl } from "@shared/utils";
import { t } from "i18next";
import { isPossiblePhoneNumber } from "react-phone-number-input";
import { toast } from "sonner";

const MAX_WORDS_TAGLINE = 10;

const sanitizeMarkup = (text: string) => {
  return text?.replace(/<\/?[^>]+(>|$)/g, "")?.trim();
};

export const getComponentsValidityFields = ({
  component,
  events,
}: {
  component: string;
  events: { tagline: string }[];
}) => {
  switch (component) {
    case "providerEvent":
      return events?.map((event, indexEvent) => [
        t("tagline"),
        `event_tagline_${indexEvent}`,
        event.tagline,
        {
          maxWords: MAX_WORDS_TAGLINE,
        },
        indexEvent,
      ]);
    case "partnerEvent":
      return events?.map((event, indexEvent) => [
        t("tagline"),
        `event_tagline_${indexEvent}`,
        sanitizeMarkup(event.tagline),
        {
          maxWords: MAX_WORDS_TAGLINE,
        },
        indexEvent,
      ]);
  }
};

export const getComponentRequiredFields = ({
  component,
  events = [],
}: {
  component: string;
  events: {
    title: string;
    type: string;
    tagline: string;
    description: string;
    startDate: string;
    endDate: string;
    startTime: string;
    endTime: string;
    cloudServices?: string;
    products?: string;
    regionId: string;
  }[];
}) => {
  switch (component) {
    case "providerEvent":
      return events.map((event, indexEvent) => [
        [
          t("event_title"),
          `event_title_${indexEvent}`,
          event.title,
          indexEvent,
        ],
        [t("event_type"), "event_type", event.type, indexEvent],
        [
          t("tagline"),
          `event_tagline_${indexEvent}`,
          event.tagline,
          indexEvent,
        ],
        [
          t("description"),
          `event_description_${indexEvent}`,
          event.description,
          indexEvent,
        ],
        [
          t("start_date"),
          `start_date_${indexEvent}`,
          event.startDate,
          indexEvent,
        ],
        [t("end_date"), `end_date_${indexEvent}`, event.endDate, indexEvent],
        [
          t("start_time"),
          `start_time_${indexEvent}`,
          event.startTime,
          indexEvent,
        ],
        [t("end_time"), `end_time_${indexEvent}`, event.endTime, indexEvent],
        [
          t("cloud_services"),
          `cloud_services_${indexEvent}`,
          event.cloudServices,
          indexEvent,
        ],
        [t("regions"), `regionId_${indexEvent}`, event.regionId, indexEvent],
      ]);
    case "partnerEvent":
      return events.map((event, indexEvent) => [
        [
          t("event_title"),
          `event_title_${indexEvent}`,
          event.title,
          indexEvent,
        ],
        [t("event_type"), `event_type_${indexEvent}`, event.type, indexEvent],
        [
          t("tagline"),
          `event_tagline_${indexEvent}`,
          sanitizeMarkup(event.tagline) || "",
          indexEvent,
        ],
        [
          t("description"),
          `event_description_${indexEvent}`,
          sanitizeMarkup(event.description) || "",
          indexEvent,
        ],
        [
          t("start_date"),
          `start_date_${indexEvent}`,
          event.startDate,
          indexEvent,
        ],
        [t("end_date"), `end_date_${indexEvent}`, event.endDate, indexEvent],
        [
          t("start_time"),
          `start_time_${indexEvent}`,
          event.startTime,
          indexEvent,
        ],
        [t("end_time"), `end_time_${indexEvent}`, event.endTime, indexEvent],
        [t("products"), `products_${indexEvent}`, event.products, indexEvent],
        [t("regions"), `region_id_${indexEvent}`, event.regionId, indexEvent],
      ]);
    default:
      return [];
  }
};

const buildRequiredField = (
  name: string,
  id: string,
  value: unknown,
  tabSelected: string
) => ({
  name: t(name),
  id,
  value,
  tabSelected,
});
const buildValidityField = (
  name: string,
  id: string,
  value: unknown,
  isValid: boolean,
  tabSelected?: string
) => ({
  name: t(name),
  id,
  value,
  isValid,
  tabSelected,
});

export const logicForRequiredFields = ({
  requiredFields,
  getComponentsRequiredFields,
}) => {
  const requiredFieldsToAdd = getComponentsRequiredFields;

  requiredFieldsToAdd
    ?.filter((el) => !!el)
    ?.forEach((el) => {
      if (Array.isArray(el[0])) {
        el.forEach((el) => {
          const [name, id, value, selectedTab] = el;
          const existingField = requiredFields.find(
            (field) => field.name === name && field.id === id
          );
          if (existingField) {
            existingField.value = value;
          } else {
            requiredFields.push(
              buildRequiredField(name, id, value, selectedTab)
            );
          }
        });
      } else {
        const [name, id, value, selectedTab] = el;
        requiredFields.push(buildRequiredField(name, id, value, selectedTab));
      }
    });
};

const isFieldValid = (value, type) => {
  if (type?.hasOwnProperty("maxWords")) {
    return value?.trim()?.split(/\s+/).length <= type.maxWords;
  }

  if (type?.hasOwnProperty("minNum") && type?.hasOwnProperty("maxNum")) {
    const otherErps = type?.otherErpNumbers?.otherErps || type?.otherErpNumbers;
    const activeOtherErps = Array.isArray(otherErps)
      ? otherErps
      : otherErps?.split(";") || [];

    return (
      value?.trim()?.length <= type.maxNum &&
      value?.trim()?.length >= type.minNum &&
      !type?.providerERPs?.includes(value?.trim()) &&
      !activeOtherErps.includes(value?.trim())
    );
  }

  if (type === "url") {
    return value?.length > 0
      ? isValidUrl(value) &&
          !(
            isYoutubeUrl(value) ||
            isVimeoUrl(value) ||
            isBrightcoveUrl(value) ||
            isBroadcomVideoUrl(value)
          )
      : true;
  }

  if (type === "email") {
    return Array.isArray(value)
      ? value.every((e) => isValidEmail(e))
      : isValidEmail(value);
  }

  if (type === "video") {
    return (
      isYoutubeUrl(value) ||
      isVimeoUrl(value) ||
      isBrightcoveUrl(value) ||
      isBroadcomVideoUrl(value)
    );
  }

  if (type === "youtube") {
    return isYoutubeUrl(value);
  }

  if (type === "linkedin") {
    return isValidLinkedin(value);
  }

  if (value && type === "phone") {
    return isPossiblePhoneNumber(String(value));
  }

  if (value && type === "phone-notRequired") {
    if (Array.isArray(value)) {
      return value?.every(
        (phoneNumber) =>
          (phoneNumber && phoneNumber.length === 0) ||
          !phoneNumber[0] ||
          isPossiblePhoneNumber(String(phoneNumber))
      );
    } else {
      return (
        (!!value && value.length === 0) ||
        !value ||
        isPossiblePhoneNumber(String(value))
      );
    }
  }

  return true;
};

const willBuildValidityField = (el, fieldsValidity, setFieldsValidity) => {
  const [name, id, value, type, tabSelected] = el;

  const existingField = fieldsValidity.find((field) =>
    ["email", "website", "phone"].includes(String(id).toLowerCase())
      ? String(field.id).toLowerCase() === String(id).toLowerCase() &&
        String(field.name).toLowerCase() === String(name).toLowerCase()
      : String(field.name).toLowerCase() === String(name).toLowerCase() &&
        field.id === id
  );

  if (existingField) {
    existingField.value = value;
    existingField.isValid = isFieldValid(value, type);
    setFieldsValidity(
      fieldsValidity?.map((field: any) => {
        if (field.name === name && field.id === id) {
          return {
            existingField,
          };
        }
        return field;
      })
    );
  } else {
    if (id !== undefined)
      fieldsValidity.push(
        buildValidityField(
          name,
          id,
          value,
          isFieldValid(value, type),
          tabSelected
        )
      );
  }
};

export const logicForValidityFields = ({
  fieldsValidity,
  getComponentsValidityFields,
  setFieldsValidity = (a: Array<any>) => {},
}) => {
  const validityFieldsToAdd = getComponentsValidityFields;

  if (validityFieldsToAdd?.length === 0) setFieldsValidity([]);
  validityFieldsToAdd
    ?.filter((el) => !!el)
    ?.forEach((el) => {
      willBuildValidityField(el, fieldsValidity, setFieldsValidity);
    });
};

export const normalizeObject = (obj) => {
  return Object.keys(obj)
    .sort()
    .reduce((acc, key) => {
      acc[key] = obj[key];
      return acc;
    }, {});
};

export const isYoutubeUrl = (url) => {
  const youtubeRegex =
    /^(?:(?:https?:)?\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
  return url.match(youtubeRegex);
};

export const isVimeoUrl = (url) => url.toLowerCase().includes("vimeo.com");

export const isValidLinkedin = (url) => {
  const regex =
    /^(https?:\/\/)?([a-z]{2,3}\.)?linkedin\.com\/(in|company)\/[\w\-._~:/?#\[\]@!$&'()*+,;=%À-ÖØ-öø-ÿ]+\/?$/;

  return url.match(regex);
};

export const previousVal = (dataArray) => {
  if (dataArray?.length === 0) {
    return "";
  }

  return dataArray?.map((c) => c?.label)?.join(", ") || "";
};

export const buildErrorMessage = ({
  field,
  isDefaultMessage = true,
  inField = false,
}) => {
  //when using different components, pass it
  const [___, option] = field.split("-");

  if (inField && isDefaultMessage && option === "valid")
    return `This field is not valid`;
  if (inField && isDefaultMessage && option === "required")
    return `This field is required`;

  if (isDefaultMessage && option === "valid")
    return `There are invalid fields.`;
  if (isDefaultMessage && option === "required")
    return `There are empty required fields.`;

  return "";
};

export const checkError = ({
  field,
  requiredFields,
  fieldsValidity,
  fieldIndex = -1,
  id = "",
}) => {
  const requiredField = requiredFields?.find((fieldReq) => {
    const value = fieldReq?.value;

    if (Number(fieldIndex) >= 0) {
      return (
        String(fieldReq.id).toLowerCase() === String(field).toLowerCase() &&
        (value[fieldIndex]?.length === 0 ||
          value[fieldIndex] === false ||
          !value[fieldIndex])
      );
    }

    if (
      value !== null &&
      value !== undefined &&
      value !== "" &&
      !isNaN(Number(value)) &&
      String(fieldReq.id).toLowerCase() === String(field).toLowerCase() &&
      !Array.isArray(value)
    ) {
      return (
        Number(value) < 0 ||
        value === false || // Explicitly check for false
        value.length === 0
      );
    }

    return (
      String(fieldReq.id).toLowerCase() === String(field).toLowerCase() &&
      (value?.length === 0 || value === false || !value)
    );
  });

  const fieldValidity = fieldsValidity?.find((fieldVal) => {
    const value = fieldVal?.value;

    if (Number(fieldIndex) >= 0) {
      return (
        String(fieldVal.name).toLowerCase() === String(field).toLowerCase() &&
        fieldVal?.id === id &&
        (value?.length === 0 || value === false || !fieldVal?.isValid)
      );
    }

    if (id?.length > 0) {
      return (
        String(fieldVal.id).toLowerCase() === String(field).toLowerCase() &&
        fieldVal?.name === id &&
        !fieldVal.isValid &&
        value?.length !== 0
      );
    }

    return (
      String(fieldVal.id).toLowerCase() === String(field).toLowerCase() &&
      (!fieldVal.isValid || value === false) &&
      value?.length !== 0
    );
  });

  return (
    (!!requiredField
      ? buildErrorMessage({
          field: `${String(requiredField?.name)}-required`,
          inField: true,
        })
      : "") ||
    (!!fieldValidity && !fieldValidity?.isValid
      ? buildErrorMessage({
          field: `${String(field)}-valid`,
          inField: true,
        })
      : "")
  );
};

export const checkValidityFields = ({
  fieldsValidity,
  setTabSelected = (_a: number) => {},
}) => {
  const field = fieldsValidity?.find((field) => !field.isValid);
  if (!!field) {
    if (setTabSelected) setTabSelected(field?.tabSelected);
    toast.error(
      buildErrorMessage({
        field: ["url", "email", "website", "phone", "link"]?.includes(
          String(field?.id).toLowerCase()
        )
          ? `${String(field?.id)}-valid`
          : `${String(field?.name)}-valid`,
      })
    );
    return true;
  }
  return false;
};

export const checkRequiredFields = ({
  requiredFields,
  setTabSelected = (_a: number) => {},
}) => {
  const field = requiredFields?.find((field) => {
    const value = field?.value;

    // Numeric validation
    if (
      value !== null &&
      value !== undefined &&
      value !== "" &&
      value !== false &&
      !isNaN(Number(value)) &&
      !Array.isArray(value)
    ) {
      return Number(value) < 0; // Negative values are invalid
    }

    // Explicit check for false
    if (value === false) {
      return true; // `false` is invalid
    }

    // General validation for other types
    return (
      value === null || value === undefined || value.length === 0 || !value
    );
  });

  if (!!field) {
    if (setTabSelected) setTabSelected(field?.tabSelected);
    toast.error(
      buildErrorMessage({
        field: `${String(field?.name)}-required`,
      })
    );
    return true;
  }
  return false;
};
